import { Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { merge, filter } from 'rxjs';
import { map } from 'rxjs/operators';

import { AsyncJobActions } from '@recruitee/async-job-data';
import { AsyncJobType } from '@recruitee/async-job-types';
import { filterBoolean } from '@recruitee/common';
import {
  DashboardStatsSidebarActions,
  DashboardStatsSidebarSelectors,
  DashboardWidgetsActions,
  DashboardWidgetsSelectors,
} from '@recruitee/dashboard-data';
import { InterviewEventsActions } from '@recruitee/interview-events-data';
import { InterviewResultRequestActions } from '@recruitee/interview-result-request-data';
import { InterviewResultsActions } from '@recruitee/interview-results-data';
import { InterviewTemplateDetailsActions } from '@recruitee/interview-templates-data';
import type { StatsSidebar } from '@recruitee/stats-types';

import { widgetDefaultPositions } from './default-positions';

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  public refreshCounters$ = merge(
    this.actions$.pipe(
      ofType(
        InterviewTemplateDetailsActions.createOnePessimisticSuccess,
        InterviewTemplateDetailsActions.removeOnePessimisticSuccess,
        InterviewEventsActions.scheduleSuccess,
        InterviewEventsActions.deleteSuccess,
        InterviewEventsActions.updateSuccess,
        InterviewResultsActions.createOnePessimisticSuccess,
        InterviewResultsActions.deleteOnePessimisticSuccess,
        InterviewResultRequestActions.createOnePessimisticSuccess,
        InterviewResultRequestActions.removeOnePessimisticSuccess,
        InterviewResultRequestActions.dismissSuccess,
        InterviewResultRequestActions.revertSuccess,
        InterviewResultRequestActions.dismissAllSuccess,
      ),
    ),
    this.actions$.pipe(
      ofType(AsyncJobActions.finish),
      filter(({ payload }) => payload.job.type === AsyncJobType.BulkCandidatesDeleteCandidates),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
  ) {}

  public loadCounters(): void {
    this.store.dispatch(DashboardStatsSidebarActions.fetch());
  }

  public getCounters(): Observable<StatsSidebar> {
    return this.store.select(DashboardStatsSidebarSelectors.data).pipe(filterBoolean);
  }

  public loadOverviewWidgets() {
    this.store.dispatch(DashboardWidgetsActions.fetch());
  }

  public getOverviewWidgets(opts?: { forceInitial?: boolean }) {
    const { forceInitial = false } = opts ?? {};
    return this.store.select(DashboardWidgetsSelectors.data).pipe(
      filterBoolean,
      map(widgets => {
        const isInitial = forceInitial || widgets.every(widget => !widget.grid);
        if (isInitial) {
          return widgets.map(widget => {
            const def = widgetDefaultPositions[widget.codename];
            if (!def) return widget;

            return {
              ...widget,
              grid: def.grid,
              enabled: def.isEnabled,
            };
          });
        } else {
          return widgets;
        }
      }),
    );
  }
}
